import React from 'react';
import { twMerge } from 'tailwind-merge';

import { Button, ButtonColor } from '@videoblocks/storywind';

import {
  cn,
  getAccessibleFocusStyles,
} from '../../../builder-io/storyblocks-design-system/utils';
import { contentTypeRoutes, contentTypes } from '../../../common/Constants';
import SiteConstants from '../../../common/SiteConstants/SiteConstants';

const AFTER_EFFECTS_TEMPLATE_URL_ID = 'ae-templates';
const { FOOTAGE, TEMPLATE, MOTION_BG, PHOTO, ILLUSTRATION, VECTOR } =
  contentTypes;

interface Category {
  id: number;
  name: string;
  urlId: string;
  active: boolean;
}

interface Props {
  categories: Category[];
  contentType: string;
  mvtPages: Record<string, string>;
  linkClassName: string;
  pillButtons?: boolean;
}

const buildCategoryUrl = (
  urlId: string,
  mediaTypeRoute: string,
  mvtPages: Record<string, string>
) => {
  let url: string;

  if (
    mediaTypeRoute === contentTypeRoutes.PHOTOS ||
    mediaTypeRoute === contentTypeRoutes.ILLUSTRATIONS ||
    mediaTypeRoute === contentTypeRoutes.VECTORS
  ) {
    url =
      SiteConstants.getInstance().getSearchRoute() +
      (!mediaTypeRoute
        ? ''
        : `?media-type=${encodeURIComponent(mediaTypeRoute)}`) +
      `&categories=${encodeURIComponent(urlId)}`;
  } else {
    const mvtRoute = mvtPages[urlId];
    url = mvtRoute || `/${mediaTypeRoute}/${urlId}`;
  }

  return url;
};

const CategoryLink = ({
  url,
  name,
  pillButtons,
}: {
  url: string;
  name: string;
  pillButtons: boolean;
}) => {
  if (pillButtons) {
    return (
      <Button
        color={ButtonColor.Pill}
        href={url}
        className={cn(
          'browseOrientedCategoryLinks-link',
          getAccessibleFocusStyles()
        )}
      >
        {name}
      </Button>
    );
  }

  return (
    <div className="rounded-sm bg-gray-100 py-2 px-4 lg:mb-2">
      <a className="browseOrientedCategoryLinks-link text-black" href={url}>
        {name}
      </a>
    </div>
  );
};

export default function BrowseOrientedStockItemCategoryLinks({
  categories,
  contentType,
  mvtPages,
  linkClassName,
  pillButtons,
}: Props) {
  const contentTypeInfo = contentTypeInfoMap[contentType];
  if (!categories || !contentTypeInfo) {
    return null;
  }

  const { linkInfo, categoryName, mediaTypeRoute } = contentTypeInfo;
  return (
    <div>
      <h3 className="font-bold leading-6 text-lg">
        Browse Similar
        {categoryName}
        Categories
      </h3>
      <div
        className={twMerge(
          'browseOrientedCategoryLinks-links flex flex-row space-1 flex-wrap',
          linkClassName
        )}
      >
        <CategoryLink
          url={linkInfo.url}
          name={linkInfo.title}
          pillButtons={pillButtons}
        />
        {categories.map(({ urlId, id, name, active }) => {
          if (!active || urlId === AFTER_EFFECTS_TEMPLATE_URL_ID) {
            return null;
          }

          const url = buildCategoryUrl(urlId, mediaTypeRoute, mvtPages);

          return (
            <CategoryLink
              key={id}
              url={url}
              name={name}
              pillButtons={pillButtons}
            />
          );
        })}
      </div>
    </div>
  );
}

const contentTypeInfoMap = {
  [FOOTAGE]: {
    linkInfo: {
      id: 'mediaType',
      url: '/video/footage',
      title: 'Stock Footage',
    },
    mediaTypeRoute: contentTypeRoutes.FOOTAGE,
    categoryName: ' Stock Video ',
  },
  [TEMPLATE]: {
    linkInfo: {
      id: 'mediaType',
      url: '/video/templates',
      title: 'Templates',
    },
    mediaTypeRoute: contentTypeRoutes.TEMPLATE,
    categoryName: ' Template ',
  },
  [MOTION_BG]: {
    linkInfo: {
      id: 'mediaType',
      url: '/video/motion-backgrounds',
      title: 'Animated Backgrounds',
    },
    mediaTypeRoute: contentTypeRoutes.MOTION_BG,
    categoryName: ' Motion Background ',
  },
  [ILLUSTRATION]: {
    linkInfo: {
      id: 'mediaType',
      url: '/images/search?media-type=illustrations',
      title: 'Illustrations',
    },
    mediaTypeRoute: contentTypeRoutes.ILLUSTRATIONS,
    categoryName: ' Illustrations ',
  },
  [VECTOR]: {
    linkInfo: {
      id: 'mediaType',
      url: '/images/search?media-type=vectors',
      title: 'Vectors',
    },
    mediaTypeRoute: contentTypeRoutes.VECTORS,
    categoryName: ' Vectors ',
  },
  [PHOTO]: {
    linkInfo: {
      id: 'mediaType',
      url: '/images/search?media-type=photos',
      title: 'Photos',
    },
    mediaTypeRoute: contentTypeRoutes.PHOTOS,
    categoryName: ' Photos ',
  },
};
